'use client'

// MiniMap — a little top-down of the patch, bottom left, so you can see where
// Pino is and go and find him.
//
// Finding him is most of the reason to walk around, and before this the only
// way was to wander until you bumped into him. Now: his dot, your arrow, and
// the handful of places worth knowing, on a cream disc that matches the rest
// of the HUD.
//
// Drawn as SVG rather than canvas because it's a dozen shapes; updated off the
// world stream at ~5Hz, which is well under the rate positions arrive at, so
// it never drives a re-render of anything else.

import { useEffect, useMemo, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { POINTS_OF_INTEREST } from '../../lib/world/sceneConfig'
import { kawaii, ui } from '../../lib/ui/theme'
import { useIsMobile } from '../../lib/ui/useIsMobile'

const SIZE = 132          // px on screen
const WORLD_RADIUS = 150  // world units the disc covers, centred on origin
// How far the offering pointers sit outside the disc. Big enough that the
// arrow reads as pointing off the map rather than sitting on it.
const ARROW_MARGIN = 14
// Close enough that the pointer is no longer telling you anything you
// cannot see. Roughly a few seconds' walk.
const ARRIVED_UNITS = 14
const REFRESH_MS = 200

// The few places worth orienting by. Anything else is noise at this size.
const LANDMARK_NAMES = new Set(['shrine', 'pond', 'rave', 'casino', 'cinema', 'internet'])

// world XZ -> svg xy. North (-Z) is up, which matches how the compass words in
// the signal descriptions read.
function project(x, z) {
  const half = SIZE / 2
  return [half + (x / WORLD_RADIUS) * half, half + (z / WORLD_RADIUS) * half]
}

export function MiniMap() {
  const mobile = useIsMobile()
  const [bird, setBird] = useState(null)
  const [me, setMe] = useState(null)
  // The signed-in visitor's own offerings, as map points. Ids come from
  // OfferingsDock's poll; positions from the live entity stream, so the
  // marker is wherever the crystal actually is (including after he keeps
  // one and it gets seated in the ruins).
  const [mine, setMine] = useState([])
  const mineRef = useRef(false)
  const landmarks = useMemo(() => (POINTS_OF_INTEREST || [])
    .filter((p) => LANDMARK_NAMES.has(p.name))
    .map((p) => ({ name: p.name, xy: project(p.position[0], p.position[2]) })), [])

  // Poll rather than subscribe: positions arrive ~10Hz and we only need the
  // map to feel live, not to be frame-accurate.
  useEffect(() => {
    const tick = () => {
      const s = useGameStore.getState()
      const npc = s.npcStates?.[0]
      // setState only when a value actually moved: a still bird and a
      // still player used to re-render the whole map five times a second
      if (npc?.pos) {
        setBird((cur) => (cur && cur.x === npc.pos.x && cur.z === npc.pos.z) ? cur : { x: npc.pos.x, z: npc.pos.z })
      }
      const p = s.playerPosGetter?.()
      if (p) {
        const yaw = p.yaw ?? 0
        setMe((cur) => (cur && cur.x === p.x && cur.z === p.z && cur.yaw === yaw) ? cur : { x: p.x, z: p.z, yaw })
      }
      const ids = new Set(s.myOfferingIds || [])
      if (ids.size) {
        const spots = (s.entityStates || [])
          // Pending only: once he has judged it, the crystal has moved to
          // the ruins (or gone), and an arrow pointing at the display
          // shelf is not an errand. The markers exist to lead you back to
          // a thing still waiting in the world.
          .filter((e) => e.type === 'SACRIFICE' && e.state?.phase === 'pending' && ids.has(e.state?.offeringId))
          // World coords ride along with the projected point: the pointer
          // needs a real distance to know when to stand down, and map
          // pixels are not it.
          .map((e) => ({ xy: project(e.pos.x, e.pos.z), x: e.pos.x, z: e.pos.z }))
        mineRef.current = spots.length > 0
        setMine((cur) => {
          if (cur.length === spots.length && cur.every((m, i) => m.x === spots[i].x && m.z === spots[i].z)) return cur
          return spots
        })
      } else if (mineRef.current) {
        mineRef.current = false
        setMine([])
      }
    }
    tick()
    const id = setInterval(tick, REFRESH_MS)
    return () => clearInterval(id)
  }, [])

  const birdXY = bird ? project(bird.x, bird.z) : null
  const meXY = me ? project(me.x, me.z) : null

  // Where each pointer points, and whether it should still be pointing.
  //
  // The bearing is taken FROM YOU, not from the middle of the map. Anchored
  // at the centre it never moved as you walked, so following one only
  // worked if you happened to be standing on the origin: walk to the edge
  // and the crystal is not where the arrow said. When there is no walker
  // (camera and drone modes) the centre is the only sensible anchor left.
  //
  // And once you are on top of it the arrow has done its job: the dot
  // inside the disc says the rest, so the pointer stands down rather than
  // spinning around you.
  const pointers = mine
    .filter((m) => !(me && Math.hypot(m.x - me.x, m.z - me.z) < ARRIVED_UNITS))
    .map((m) => {
      const from = meXY || [SIZE / 2, SIZE / 2]
      return { ang: Math.atan2(m.xy[1] - from[1], m.xy[0] - from[0]) }
    })

  // On a phone the map leaves the bottom-left entirely: that corner is
  // where the walking thumb lands, and a 132px disc there was a third of
  // the screen sitting on top of the status pill, the controls hint AND
  // the stick's spawn zone. It tucks under the menu button instead, at
  // 92px: same 132-unit viewBox, so everything inside just scales.
  const disp = mobile ? 92 : SIZE
  const s = disp / SIZE

  return (
    <div
      className="fixed top-[124px] right-3 bottom-auto left-auto md:top-auto md:right-auto md:bottom-6 md:left-6 z-40 pointer-events-none select-none"
      style={{
        width: disp,
        height: disp,
        borderRadius: '50%',
        background: kawaii.cream,
        border: kawaii.outline,
        boxShadow: kawaii.drop,
        position: 'fixed',
      }}
    >
      {/* stitch ring, same as the needs card */}
      <div
        style={{
          position: 'absolute',
          inset: 5,
          borderRadius: '50%',
          border: kawaii.stitch,
          pointerEvents: 'none',
        }}
      />

      {/* His pulse, under the map. HTML, not SVG <animate>, on purpose:
          SMIL is not composited, so the old pulse repainted the page sixty
          times a second, and that raster runs on the same GPU thread that
          draws the world (842 paints in 15 s, some flushes 150 ms; 28 Oct,
          docs/world-performance.md). A transform and opacity keyframe runs
          on the compositor and paints nothing. Clipped to the same disc the
          SVG clips to, which is 7 units in from the rim. z-index -1 puts it
          where the SVG circle was: over the cream, under the map and the
          stitch ring (the fixed container is its own stacking context). */}
      {birdXY && (
        <div style={{ position: 'absolute', inset: 7 * s, borderRadius: '50%', overflow: 'hidden', pointerEvents: 'none', zIndex: -1 }}>
          <style>{`@keyframes mm-pulse { 0%, 100% { transform: scale(0.6); opacity: 0.45 } 50% { transform: scale(1); opacity: 0.05 } }`}</style>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 20 * s,
              height: 20 * s,
              transform: `translate(${(birdXY[0] - 7 - 10) * s}px, ${(birdXY[1] - 7 - 10) * s}px)`,
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: 'rgba(240, 176, 100, 0.32)',
                animation: 'mm-pulse 2.4s linear infinite',
                willChange: 'transform, opacity',
              }}
            />
          </div>
        </div>
      )}

      <svg width={disp} height={disp} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: 'block' }}>
        <defs>
          <clipPath id="mm-clip">
            <circle cx={SIZE / 2} cy={SIZE / 2} r={SIZE / 2 - 7} />
          </clipPath>
        </defs>
        <g clipPath="url(#mm-clip)">
          {/* the places worth knowing */}
          {landmarks.map((l) => (
            <circle
              key={l.name}
              cx={l.xy[0]}
              cy={l.xy[1]}
              r={2.6}
              fill="rgba(47,38,80,0.28)"
            />
          ))}

          {/* what you left him, where it lies. The pointer for it lives
              outside the disc, in its own overlay below. */}
          {mine.map((m, i) => (
            <circle key={`mine-${i}`} cx={m.xy[0]} cy={m.xy[1]} r={3.2} fill="#d99a2b" stroke={kawaii.ink} strokeWidth="1.4" />
          ))}

          {/* you */}
          {meXY && (
            <g transform={`translate(${meXY[0]} ${meXY[1]}) rotate(${(-(me.yaw ?? 0) * 180) / Math.PI})`}>
              <path d="M0,-6 L4.4,5 L0,2.6 L-4.4,5 Z" fill="#8fb8e8" stroke={kawaii.ink} strokeWidth="1.6" strokeLinejoin="round" />
            </g>
          )}

          {/* him */}
          {birdXY && (
            <circle cx={birdXY[0]} cy={birdXY[1]} r={4.4} fill="#f0b064" stroke={kawaii.ink} strokeWidth="2" />
          )}
        </g>
      </svg>

      {/* Pointers to your offerings, sitting outside the disc.
          They lived just inside the rim before, where the eye reads them
          as part of the map rather than as directions off it. This overlay
          is larger than the disc and unclipped, so they can overhang. */}
      {pointers.length > 0 && (
        <svg
          width={(SIZE + ARROW_MARGIN * 2) * s}
          height={(SIZE + ARROW_MARGIN * 2) * s}
          viewBox={`0 0 ${SIZE + ARROW_MARGIN * 2} ${SIZE + ARROW_MARGIN * 2}`}
          style={{ position: 'absolute', left: -ARROW_MARGIN * s, top: -ARROW_MARGIN * s, overflow: 'visible', pointerEvents: 'none' }}
        >
          {pointers.map((p, i) => {
            const c = SIZE / 2 + ARROW_MARGIN        // centre in the bigger canvas
            const r = SIZE / 2 + ARROW_MARGIN * 0.45 // just beyond the disc edge
            const ax = c + Math.cos(p.ang) * r
            const ay = c + Math.sin(p.ang) * r
            return (
              <g key={`ptr-${i}`} transform={`translate(${ax} ${ay}) rotate(${(p.ang * 180) / Math.PI + 90})`}>
                <path
                  d="M0,-9 L7,6 L0,2.2 L-7,6 Z"
                  fill="#d99a2b"
                  stroke={kawaii.ink}
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </g>
            )
          })}
        </svg>
      )}

      {!bird && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: kawaii.inkSoft,
            fontFamily: ui.font,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          finding him
        </div>
      )}
    </div>
  )
}

export default MiniMap
